import React, {
  useEffect,
} from "react";
import { motion } from "framer-motion";
import {
  ChevronRightIcon,
  FlagIcon,
  FolderOpenIcon,
  PlusIcon,
  TrophyIcon,
  UsersIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

export default function StartPage() {
  const navigate = useNavigate();

  const { user, loading } =
    useAuth();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [
    user,
    loading,
    navigate,
  ]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4">
        <div className="flex items-center gap-3 rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-5 py-4 text-[#1f2d26] shadow-sm">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
            ⛳
          </span>

          <div>
            <p className="text-sm font-semibold">
              Laddar HoleInOne
            </p>

            <p className="text-xs text-[#68716c]">
              Förbereder din
              startsida…
            </p>
          </div>
        </div>
      </div>
    );
  }

  const actions = [
    {
      title:
        "Skapa turnering",
      description:
        "Sätt upp spelare, ronder, spelformer och sidotävlingar.",
      icon: PlusIcon,
      onClick: () =>
        navigate("/ny"),
      primary: true,
    },

    {
      title:
        "Mina turneringar",
      description:
        "Öppna, följ och administrera dina turneringar.",
      icon: FolderOpenIcon,
      onClick: () =>
        navigate("/mina"),
    },

    {
      title:
        "Spelarhantering",
      description:
        "Lägg till spelare och uppdatera handicap eller klubb.",
      icon: UsersIcon,
      onClick: () =>
        navigate("/spelare"),
    },

    {
      title: "Golfbanor",
      description:
        "Sök bland banor eller lägg till en egen golfbana.",
      icon: FlagIcon,
      onClick: () =>
        navigate("/courses"),
    },
  ];

  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />

        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <main className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 sm:py-12">
        <motion.div
          className="w-full"
          initial={{
            opacity: 0,
            y: 16,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.45,
            ease: "easeOut",
          }}
        >
          <section className="overflow-hidden rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_22px_65px_rgba(31,45,38,0.11)]">
            <div className="relative overflow-hidden bg-[#183d2d] px-5 py-7 text-[#fffdf8] sm:px-8 sm:py-9">
              <div className="pointer-events-none absolute -right-20 -top-28 h-72 w-72 rounded-full bg-[#b08b45]/20 blur-3xl" />

              <div className="pointer-events-none absolute -bottom-28 -left-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

              <div className="relative flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-sm backdrop-blur-sm">
                      <img
                        src="https://yswafknmqpwjrsxjslbu.supabase.co/storage/v1/object/public/logos//logosmall.png"
                        alt="HoleInOne"
                        className="h-8 w-8 object-contain brightness-125"
                      />
                    </div>

                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/55">
                        HoleInOne.se
                      </p>

                      <p className="mt-0.5 text-sm font-medium text-white/80">
                        Tournament
                        manager
                      </p>
                    </div>
                  </div>

                  <p className="mt-7 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#d7c28e]">
                    Välkommen tillbaka
                  </p>

                  <h1 className="mt-2 max-w-xl text-3xl font-semibold tracking-tight sm:text-4xl">
                    Allt du behöver
                    för nästa
                    golftävling.
                  </h1>

                  <p className="mt-3 max-w-xl text-sm leading-6 text-white/65 sm:text-base">
                    Skapa
                    turneringar,
                    hantera
                    deltagare och
                    följ resultaten
                    från första utslag
                    till sista putt.
                  </p>
                </div>

                <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.07] px-4 py-3 backdrop-blur-sm">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#b08b45] text-white">
                    <TrophyIcon className="h-5 w-5" />
                  </span>

                  <div>
                    <p className="text-xs font-semibold text-white">
                      Redo för nästa
                      runda
                    </p>

                    <p className="mt-0.5 text-[11px] text-white/55">
                      Välj ett
                      alternativ nedan
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 py-5 sm:px-6 sm:py-7">
              <div className="mb-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#9a7735]">
                  Snabbval
                </p>

                <h2 className="mt-1 text-xl font-semibold text-[#183d2d]">
                  Vad vill du göra?
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {actions.map(
                  (
                    action,
                    index
                  ) => {
                    const Icon =
                      action.icon;

                    return (
                      <motion.button
                        key={
                          action.title
                        }
                        type="button"
                        onClick={
                          action.onClick
                        }
                        className={`group min-w-0 rounded-2xl border p-4 text-left transition ${
                          action.primary
                            ? "border-[#183d2d] bg-[#183d2d] text-white shadow-[0_10px_24px_rgba(24,61,45,0.17)] hover:bg-[#214d39]"
                            : "border-[#d8d2c4] bg-[#fffdf8] text-[#1f2d26] hover:-translate-y-0.5 hover:border-[#b8b09f] hover:shadow-md"
                        }`}
                        initial={{
                          opacity: 0,
                          y: 12,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                        }}
                        transition={{
                          duration: 0.35,
                          delay:
                            0.08 +
                            index *
                              0.06,
                        }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <span
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                              action.primary
                                ? "bg-white/12 text-[#e9d39e]"
                                : "bg-[#e5ede7] text-[#183d2d]"
                            }`}
                          >
                            <Icon className="h-5 w-5" />
                          </span>

                          <ChevronRightIcon
                            className={`mt-1 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 ${
                              action.primary
                                ? "text-white/55"
                                : "text-[#9a958a]"
                            }`}
                          />
                        </div>

                        <h3 className="mt-5 text-base font-semibold">
                          {
                            action.title
                          }
                        </h3>

                        <p
                          className={`mt-1.5 text-xs leading-5 ${
                            action.primary
                              ? "text-white/60"
                              : "text-[#68716c]"
                          }`}
                        >
                          {
                            action.description
                          }
                        </p>
                      </motion.button>
                    );
                  }
                )}
              </div>
            </div>

            <footer className="flex flex-col gap-2 border-t border-[#e4ded1] bg-[#f7f4ec] px-5 py-4 text-[10px] text-[#7b817d] sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p>
                ©{" "}
                {new Date().getFullYear()}{" "}
                HoleInOne.se
              </p>

              <p>
                En tjänst från
                Hollander Web
              </p>
            </footer>
          </section>
        </motion.div>
      </main>
    </div>
  );
}